import { Link, useNavigate } from 'react-router-dom';
import 'react-loading-skeleton/dist/skeleton.css';
import React, { useState, useEffect } from 'react';
import { VoucherService } from '../../services';
import { Alert } from '../../services';
import Pagination from '../../components/Pagination';
import ConfirmationPop from '../../components/ConfirmationPop';
import { PlusIcon } from '../../components/icons/IconProvider';
import NoDataFound from '../../components/NoDataFound';
import moment from 'moment';
import TableComponent from '../../components/TableComponent';
import StatusBadge from '../../components/StatusBadge';
import SortByFilter from '../../components/listing/SortByFilter';
import SearchInput from '../../components/SearchInput';

const ManageVoucher = () => {
  const [voucherStatus, setVoucherStatus] = useState(0);
  const [voucherId, setVoucherId] = useState(0);
  const [isLoader, setLoader] = useState(false);
  const [voucherDetail, setVoucherDetail] = useState([]);
  const [page, setPage] = useState(1);
  const [, setTotalPages] = useState(1);
  const [perPage] = useState(10);
  const [isPagination, setPagination] = useState({
    totalPages: 10,
    currentPage: 1,
  });
  const [commonSearch, setCommonSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [isActive, setIsActive] = useState('');
  const [isCommonConfirmationPop, setCommonConfirmationPop] = useState(false);
  const [isConfirmationPopData, setConfirmationPopData] = useState({
    FirstText: '',
    SecondText: '',
    FirstButton: '',
    secondButton: '',
    slug: '',
  });

  const [filterData, setFilterData] = useState({
    perPage: perPage,
    page: page,
    commonSearch: '',
    sortBy: 'new_to_old',
    status: isActive,
  });

  const navigate = useNavigate();
  const tableConfig = {
    columns: {
      voucher_code: {
        title: 'Voucher Code',
        render: (data) => {
          return (
            <span className="font-medium">{data?.voucher_code || 'NA'}</span>
          );
        },
      },
      project: {
        title: 'Project',
        render: (data) => {
          return data?.project?.name || 'NA';
        },
      },
      expiry_date: {
        title: 'Expiry Date',
        render: (data) => {
          return moment(data.expiry_date).format('DD MMM YYYY');
        },
      },
      is_voucher_used: {
        title: 'Is Voucher Used',
        render: (data) => {
          return (
            <StatusBadge status={data?.totalTokenUtilized > 0 ? 'yes' : 'no'} />
          );
        },
      },
      token_utilization: {
        title: 'Token Utilization',
        render: (data) => {
          return `${data.totalTokenUtilized} of ${data.token_alloted}`;
        },
      },
      status: {
        title: 'Status',
        render: (data) => {
          const handleCheckboxChange = (e) => {
            e.stopPropagation();
            changeStatus(data.id, data.is_active == 0 ? 1 : 0);
          };

          return (
            <div className="cursor-pointer" onClick={handleCheckboxChange}>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.is_active == 1}
                  className="sr-only peer opacity-0"
                  onChange={handleCheckboxChange} // Attaching the modified handler to the checkbox
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#ebfff3] dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#22c65f]"></div>
              </label>
            </div>
          );
        },
      },
    },
    data: voucherDetail,
  };

  useEffect(() => {
    getVoucherList();
  }, [filterData]);

  const getVoucherList = async () => {
    try {
      setLoader(true);
      let voucherResponse = await VoucherService.getVoucherList(
        filterData.perPage,
        filterData.page,
        filterData.commonSearch,
        filterData.sortBy,
        filterData.status,
      );

      if (voucherResponse.status === 'failure') {
        setLoader(false);
        Alert.error(voucherResponse.message);
        return false;
      }

      setPagination({
        totalPages: voucherResponse.data.pagination.totalPages,
        currentPage: voucherResponse.data.pagination.page,
      });
      setVoucherDetail(voucherResponse.data.records);
      setTotalPages(voucherResponse.data.pagination.totalPages);
      setPage(voucherResponse.data.pagination.page);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  const handlePagination = (current) => {
    setPagination({ ...isPagination, currentPage: current });
    setFilterData({
      ...filterData,
      page: current,
    });
  };

  const resetFilter = () => {
    setSortBy('');
    setIsActive('');
    setCommonSearch('');
    setFilterData({
      ...filterData,
      commonSearch: '',
      sortBy: '',
      status: '',
    });
  };

  const changeStatus = async (voucher_id, status) => {
    setVoucherId(voucher_id);
    setVoucherStatus(status);

    let message = '';
    let firstText = '';
    let secondButton = '';

    if (status === 1) {
      firstText = 'Activate Voucher';
      secondButton = 'Activate';
      message = 'Are you sure, Do you want to activate this voucher?';
    }
    if (status === 0) {
      firstText = 'Deactivate Voucher';
      secondButton = 'Deactivate';
      message = 'Are you sure, Do you want to deactivate this voucher?';
    }

    setConfirmationPopData({
      FirstText: firstText,
      SecondText: message,
      FirstButton: "I'll do it later",
      secondButton: secondButton,
      slug: 'redirect',
    });
    setCommonConfirmationPop(true);
    return false;
  };

  const updateStatus = async () => {
    try {
      const formData = {
        voucher_id: voucherId,
        status: voucherStatus,
      };
      let voucherResponse = await VoucherService.updateVoucherStatus(formData);

      if (voucherResponse.status === 'failure') {
        setLoader(false);
        Alert.error(voucherResponse.message);
      } else {
        Alert.success(voucherResponse.message);
      }
      // Reload listing
      getVoucherList();
    } catch (error) {
      Alert.error(error.message);
      return false;
    }
  };

  const confirmationPopHandel = async (slug) => {
    if (slug === 'redirect') {
      setCommonConfirmationPop(false);
      updateStatus();
      return false;
    }
  };
  const statusOptions = [
    { name: 'All', value: '' },
    { name: 'Active', value: '1' },
    { name: 'Inactive', value: '0' },
  ];

  const sortByOptions = [
    { name: 'Sort By', value: '' },
    { name: 'Latest To Old', value: 'new_to_old' },
    { name: 'Old To Latest', value: 'old_to_new' },
  ];
  const voucherRedirect = (project_id) => {
    navigate('/vouchers/view/' + project_id);
  };

  return (
    <div className="">
      <div className="grid grid-cols-2 mb-4">
        <h4 className="">Vouchers Management</h4>
        <div className="flex justify-end items-center"></div>
      </div>

      <div className="w-full bg-brandGray px-4 md:px-5 py-4 rounded-xl">
        <div className="lg:grid lg:grid-cols-3 mb-2 lg:mb-0 ">
          <div className="mb-2 lg:mb-0">
            <Link
              to="/vouchers/addedit"
              className="bg-brandBlue text-white w-full md:w-auto flex md:inline-flex items-center py-[9px] px-4 rounded-lg md:mr-3 mb-2 md:mb-0"
            >
              <PlusIcon />
              <span className="text-base leading-none">New Voucher</span>
            </Link>
          </div>
          <div className="lg:flex items-center md:justify-end gap-3 md:col-span-2">
            <SortByFilter
              value={filterData.status}
              dropdownList={statusOptions}
              filterData={filterData}
              setFilterData={setFilterData}
              setPage={setPage}
              fieldName="status"
            />

            <SortByFilter
              value={filterData.sortBy}
              dropdownList={sortByOptions}
              filterData={filterData}
              setFilterData={setFilterData}
              setPage={setPage}
              fieldName="sortBy"
            />
            <div className="flex sm:inline-flex relative mb-2 sm:mb-0">
              <SearchInput
                placeholder="Search By Voucher Name / Project Name"
                name="commonSearch"
                id="commonSearch"
                onChange={(e) => {
                  setPage(1);
                  setFilterData({
                    ...filterData,
                    page: 1,
                    commonSearch: e.target.value,
                  });
                  setCommonSearch(e.target.value);
                }}
                value={commonSearch}
              />
            </div>

            <p
              className="text-blue-700 bg-blue-500 ml-2 lg:ml-0 bg-opacity-5 rounded font-semibold text-sm inline-block cursor-pointer py-3 h-[42px] px-2 whitespace-nowrap"
              onClick={() => resetFilter()}
            >
              Clear filters
            </p>
          </div>
        </div>

        <div className="flex flex-col relative overflow-x-auto w-full">
          <TableComponent
            tableConfig={tableConfig}
            isListLoader={isLoader}
            isList={voucherDetail}
            redirectTo={voucherRedirect}
          />
          {voucherDetail && voucherDetail.length > 0 && isLoader === false && (
            <Pagination
              total={isPagination.totalPages}
              current={isPagination.currentPage}
              pagination={(crPage) => handlePagination(crPage)}
            />
          )}
          {voucherDetail &&
            voucherDetail.length === 0 &&
            isLoader === false && (
              <>
                <NoDataFound />
              </>
            )}
        </div>
      </div>
      <ConfirmationPop
        isPop={isCommonConfirmationPop}
        setPop={setCommonConfirmationPop}
        functionCall={confirmationPopHandel}
        FirstText={isConfirmationPopData.FirstText}
        secondText={isConfirmationPopData.SecondText}
        FirstButton={isConfirmationPopData.FirstButton}
        secondButton={isConfirmationPopData.secondButton}
        slug={isConfirmationPopData.slug}
      />
    </div>
  );
};
export default ManageVoucher;
